import type { LicenseState, TrialState } from '../types';

/** Trial duration in days */
export const TRIAL_DURATION_DAYS = 14;

/** Reminder interval: show again after this many days since last dismissed */
export const TRIAL_REMINDER_INTERVAL_DAYS = 7;

/**
 * "Maybe later" snooze window for the passive start-page trial card: after the
 * user dismisses it, the card stays hidden for this many days, then reappears.
 */
export const TRIAL_CARD_DISMISS_INTERVAL_DAYS = 14;

/**
 * Derive the current trial state from license state.
 * This is the single source of truth for trial status.
 *
 * ACCOUNT-BASED TRIAL:
 * If `currentUserId` is provided, the trial is only considered valid
 * if `state.trialUserId` matches `currentUserId`. This prevents trial
 * leakage between different user accounts.
 *
 * @param state - The license state to check
 * @param currentUserId - Optional current user ID to verify trial ownership
 * @returns The derived trial state
 */
export function getTrialState(state: LicenseState, currentUserId?: string): TrialState {
  // If trial was explicitly skipped, return 'skipped'
  if (state.trialSkipped) {
    return 'skipped';
  }

  // If trial hasn't started yet
  if (!state.trialStartedAt) {
    return 'not-started';
  }

  // ACCOUNT-BASED TRIAL CHECK:
  // If we know the current user and the trial has a user ID,
  // verify they match. If they don't match, the trial belongs
  // to a different user and should not be used.
  if (currentUserId && state.trialUserId && state.trialUserId !== currentUserId) {
    return 'not-started';
  }

  // Trial was started, check if expired
  const now = new Date();
  const expiresAt = state.trialExpiresAt ? new Date(state.trialExpiresAt) : null;

  if (expiresAt && now >= expiresAt) {
    return 'expired';
  }

  return 'active';
}

/**
 * Calculate trial expiry date (14 days from start).
 */
export function calculateTrialExpiry(startDate: Date): Date {
  const expiryDate = new Date(startDate);
  expiryDate.setDate(expiryDate.getDate() + TRIAL_DURATION_DAYS);
  return expiryDate;
}

/**
 * Get the number of days remaining in the trial.
 * Returns 0 if trial is not active.
 */
export function getTrialDaysRemaining(state: LicenseState): number {
  if (getTrialState(state) !== 'active') {
    return 0;
  }

  if (!state.trialExpiresAt) {
    return 0;
  }

  const now = new Date();
  const expiresAt = new Date(state.trialExpiresAt);
  const diffMs = expiresAt.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Check if the trial reminder should be shown.
 * Returns true if:
 * - Trial was skipped (user chose Personal mode)
 * - AND it has been more than 7 days since the reminder was last dismissed
 *
 * Note: When the user first skips the trial, `trialReminderDismissedAt` is set
 * to that moment (see `skipTrial()` in licenseStore), so the reminder won't
 * show for 7 days after they chose Personal mode.
 */
export function shouldShowTrialReminder(state: LicenseState): boolean {
  // Only show reminder for skipped trials
  if (getTrialState(state) !== 'skipped') {
    return false;
  }

  // If no timestamp recorded, the state is inconsistent (shouldn't happen
  // since skipTrial() sets this), but default to not showing
  if (!state.trialReminderDismissedAt) {
    return false;
  }

  const now = new Date();
  const lastDismissed = new Date(state.trialReminderDismissedAt);
  const daysSinceDismissed = Math.floor(
    (now.getTime() - lastDismissed.getTime()) / (1000 * 60 * 60 * 24)
  );

  return daysSinceDismissed >= TRIAL_REMINDER_INTERVAL_DAYS;
}

/**
 * Whether the passive start-page trial card should be shown, based purely on
 * trial state + the "Maybe later" snooze. Environmental gates (browser mode,
 * Pro access, test mode) are the caller's responsibility.
 *
 * Shown only while the trial decision is still open ('not-started') AND the
 * card has not been snoozed via "Maybe later" within the last
 * TRIAL_CARD_DISMISS_INTERVAL_DAYS days. Unlike skipping (permanent), the
 * snooze lapses so the card returns.
 */
export function shouldShowTrialCard(
  state: LicenseState,
  currentUserId?: string,
  now: Date = new Date()
): boolean {
  if (getTrialState(state, currentUserId) !== 'not-started') {
    return false;
  }

  if (!state.trialCardDismissedAt) {
    return true;
  }

  const lastDismissed = new Date(state.trialCardDismissedAt);
  const daysSinceDismissed = Math.floor(
    (now.getTime() - lastDismissed.getTime()) / (1000 * 60 * 60 * 24)
  );

  return daysSinceDismissed >= TRIAL_CARD_DISMISS_INTERVAL_DAYS;
}

/**
 * Create trial start state: sets start and expiry dates.
 */
export function createTrialStartState(): Pick<
  LicenseState,
  'trialStartedAt' | 'trialExpiresAt' | 'trialSkipped' | 'mode'
> {
  const now = new Date();
  return {
    trialStartedAt: now,
    trialExpiresAt: calculateTrialExpiry(now),
    trialSkipped: false,
    mode: 'pro', // During trial, user has Pro access
  };
}

/**
 * Create trial skip state: marks trial as skipped.
 */
export function createTrialSkipState(): Pick<LicenseState, 'trialSkipped' | 'mode'> {
  return {
    trialSkipped: true,
    mode: 'personal',
  };
}

/**
 * Whether the first-time trial onboarding dialog is due to auto-open.
 *
 * The dialog never auto-opens during the install-day session — day 1 shows
 * only the passive "free for personal use" card on the start page. From the
 * first session on a LATER calendar day, the dialog is due.
 *
 * `firstSeenAt === undefined` means the install predates the field (the user
 * has already been seeing the boot dialog) → due, preserving the status quo
 * for existing installs.
 */
export function isTrialOnboardingDue(
  firstSeenAt: number | undefined,
  now: Date = new Date()
): boolean {
  if (firstSeenAt === undefined) return true;
  return new Date(firstSeenAt).toDateString() !== now.toDateString();
}

/**
 * Whether the trial-expiry dialog is due. Pure gate for the hook: the dialog
 * appears ONCE per distinct trial expiry (the caller persists which expiry it
 * was shown for), only after the trial actually expired, never over Pro
 * access, never while a just-signed-in user's entitlement is still being
 * fetched (they may turn out to be a subscriber), and never before stores
 * hydrate. `trialExpiresAt` is required — an 'expired' state without a date
 * has nothing stable to key the once-per-expiry memory on, so it stays
 * silent rather than risking a nag on every boot.
 */
export function shouldShowTrialExpiryDialog(input: {
  isInitialized: boolean;
  hasProAccess: boolean;
  isResolvingEntitlement: boolean;
  trialState: TrialState;
  /** Persisted state can hold a Date or its rehydrated ISO string. */
  trialExpiresAt: Date | string | undefined;
  /** ISO string of the expiry the dialog was already shown for (persisted). */
  shownForExpiresAt: string | null;
}): boolean {
  if (!input.isInitialized) return false;
  if (input.hasProAccess) return false;
  if (input.isResolvingEntitlement) return false;
  if (input.trialState !== 'expired') return false;
  const expiryIso = trialExpiryIso(input.trialExpiresAt);
  if (!expiryIso) return false;
  return input.shownForExpiresAt !== expiryIso;
}

/** Canonical ISO key for a trial expiry (undefined for absent/invalid values). */
export function trialExpiryIso(value: Date | string | undefined): string | undefined {
  if (!value) return undefined;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? new Date(time).toISOString() : undefined;
}

/**
 * Whether the trial dialog should RE-OPEN for a persisted trial intent that
 * survived a restart / dialog close during the external-browser sign-in (see
 * `signInIntent.ts`). Pure gate — the caller consumes the persisted intent
 * only after this passes, so an intent is never burned while entitlement is
 * still resolving.
 *
 * Never re-offers while the same-session auto-start flow is in flight, while
 * the just-signed-in user's entitlement is still being fetched (they may turn
 * out to be a subscriber), once the user has Pro access, or when the trial is
 * no longer startable ('active'/'expired').
 */
export function shouldReofferTrialAfterSignIn(input: {
  isAuthenticated: boolean;
  isResolvingEntitlement: boolean;
  hasProAccess: boolean;
  trialState: TrialState;
  pendingTrialStartInFlight: boolean;
}): boolean {
  if (!input.isAuthenticated) return false;
  if (input.isResolvingEntitlement) return false;
  if (input.hasProAccess) return false;
  if (input.pendingTrialStartInFlight) return false;
  return input.trialState === 'not-started' || input.trialState === 'skipped';
}

/**
 * `auth:trial_start_server` semantics: `new` = the server CREATED this trial in
 * this action, `restored` = the server handed back a trial that already existed
 * (sign-in on another device, reinstall, empty local state). The server says
 * which — `POST /licensing/trial/start` returns `trial.is_new` — so the store
 * takes that flag; a 15-minute clock heuristic on `started_at` used to stand in
 * for it (review 2026-09-05). Token-sync / sign-in hydration paths never create
 * a trial, so they pass nothing and are always `restored`.
 */
export function classifyServerTrialStart(isNew: boolean | undefined): 'new' | 'restored' {
  return isNew === true ? 'new' : 'restored';
}
